import { useCallback, useEffect, useState } from "react";
import { BellRing, Smartphone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { currentPushSubscription, isIOS, isStandalone, pushSupport, subscribeToPush, unsubscribeFromPush } from "@/lib/pwa";

const describe = (sub) => `${sub.device}${sub.current ? " (this device)" : ""}`;

export const NotificationSettings = ({ variant = "staff", disabled = false }) => {
  const [status, setStatus] = useState(null);
  const [support] = useState(() => pushSupport());
  const [thisDevice, setThisDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const subscription = await currentPushSubscription();
      const response = await api.get("/push/status", { params: { endpoint: subscription?.endpoint || "" } });
      setStatus(response.data);
      setThisDevice(Boolean(subscription) && response.data.subscriptions.some((s) => s.current));
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function enable() {
    setBusy(true); setError(""); setMessage("");
    try {
      await subscribeToPush();
      setMessage("Notifications are on for this device.");
      await load();
    } catch (requestError) {
      setError(requestError?.message || formatApiError(requestError));
    } finally { setBusy(false); }
  }

  async function disable() {
    setBusy(true); setError(""); setMessage("");
    try {
      await unsubscribeFromPush();
      setMessage("Notifications are off for this device.");
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally { setBusy(false); }
  }

  async function sendTest() {
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await api.post("/push/test");
      setMessage(response.data.sent ? `Test notification sent to ${response.data.sent} device${response.data.sent === 1 ? "" : "s"}.` : `Nothing sent: ${response.data.skipped || "delivery failed"}.`);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally { setBusy(false); }
  }

  async function toggle(key, value) {
    setError("");
    const previous = status.settings;
    setStatus((s) => ({ ...s, settings: { ...s.settings, [key]: value } }));
    try {
      const response = await api.put("/push/settings", { categories: { [key]: value } });
      setStatus((s) => ({ ...s, settings: response.data.settings, reminder_mode: response.data.reminder_mode }));
    } catch (requestError) {
      setStatus((s) => ({ ...s, settings: previous }));
      setError(formatApiError(requestError));
    }
  }

  async function setMode(mode) {
    setError("");
    const previous = status.reminder_mode;
    setStatus((s) => ({ ...s, reminder_mode: mode }));
    try {
      const response = await api.put("/push/settings", { reminder_mode: mode });
      setStatus((s) => ({ ...s, settings: response.data.settings, reminder_mode: response.data.reminder_mode }));
    } catch (requestError) {
      setStatus((s) => ({ ...s, reminder_mode: previous }));
      setError(formatApiError(requestError));
    }
  }

  const portal = variant === "client";
  const btn = portal ? "cp-btn" : "button";
  const primary = portal ? "cp-btn cp-btn-primary" : "button button-primary";
  const secondary = portal ? "cp-btn cp-btn-outline" : "button button-secondary";
  const denied = support.supported && Notification.permission === "denied";

  return (
    <div className="notification-settings" data-testid="notification-settings">
      <div className="notification-device" data-testid="notification-device">
        <Smartphone size={16} />
        <div>
          <strong>Push notifications on this device</strong>
          {!support.supported && <p className="muted-text" data-testid="notification-unsupported">{support.reason}</p>}
          {denied && <p className="muted-text" data-testid="notification-denied">Notifications are blocked for TAGForce. Allow them in your browser or phone settings, then try again.</p>}
          {support.supported && !denied && <p className="muted-text">{thisDevice ? "On — you'll get alerts here even when TAGForce is closed." : isIOS() && !isStandalone() ? "Open TAGForce from your Home Screen icon to turn these on." : "Off on this device."}</p>}
          {status && status.subscriptions.length > 0 && (
            <ul className="notification-devices" data-testid="notification-devices">{status.subscriptions.map((s) => <li key={s.endpoint_hash}>{describe(s)}</li>)}</ul>
          )}
        </div>
        <div className="notification-device-actions">
          {support.supported && !denied && !thisDevice && <button className={primary} type="button" disabled={busy || disabled || !status?.configured} data-testid="notifications-enable" onClick={enable}><BellRing size={14} /> Turn on</button>}
          {thisDevice && <button className={secondary} type="button" disabled={busy || disabled} data-testid="notifications-disable" onClick={disable}>Turn off here</button>}
          {status && status.subscriptions.length > 0 && <button className={`${btn} ${portal ? "cp-btn-outline" : "button-secondary"}`} type="button" disabled={busy || disabled} data-testid="notifications-test" onClick={sendTest}>Send test</button>}
        </div>
      </div>
      {status && (
        <ul className="notification-categories" data-testid="notification-categories">
          {status.categories.map((category) => {
            const locked = (status.locked || []).includes(category.key);
            return (
              <li key={category.key}>
                <label className="policy-check notification-category">
                  <input type="checkbox" data-testid={`notification-toggle-${category.key}`} checked={Boolean(status.settings[category.key])} disabled={disabled || locked} onChange={(event) => toggle(category.key, event.target.checked)} />
                  <span><strong>{category.label}{locked && <em className="notification-locked" data-testid={`notification-locked-${category.key}`}> · always on</em>}</strong><small>{category.description}</small></span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      {status && variant === "provider" && (
        <fieldset className="notification-reminder-mode" data-testid="notification-reminder-mode">
          <legend>Automatic reminders</legend>
          <label className="policy-check"><input type="radio" name="reminder-mode" data-testid="reminder-mode-immediate" checked={status.reminder_mode === "immediate"} disabled={disabled} onChange={() => setMode("immediate")} /><span><strong>Immediate</strong><small>Each scheduling reminder arrives as its own email and phone alert.</small></span></label>
          <label className="policy-check"><input type="radio" name="reminder-mode" data-testid="reminder-mode-daily" checked={status.reminder_mode === "daily_summary"} disabled={disabled} onChange={() => setMode("daily_summary")} /><span><strong>Daily summary</strong><small>Reminders are bundled into one summary email each morning, with a single phone alert.</small></span></label>
        </fieldset>
      )}
      {message && <p className={portal ? "cp-success" : "form-message success-message"} data-testid="notification-message">{message}</p>}
      {error && <p className={portal ? "cp-error" : "form-message error-message"} data-testid="notification-error">{error}</p>}
    </div>
  );
};
